"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Proveedor de tema: solo alterna claro/oscuro.
 *
 * ANTES este componente también permitía sobreescribir la paleta en tiempo de
 * ejecución (`applyCustomColors`), guardándola en localStorage y aplicándola
 * como estilos EN LÍNEA sobre <html>. Se eliminó por tres motivos:
 *
 *   1. Los estilos en línea ganan al stylesheet, así que la paleta de marca
 *      definida en globals.css quedaba anulada por lo que hubiera guardado en
 *      cada navegador. Distintas personas veían distintos naranjas.
 *   2. Se aplicaban DESPUÉS del primer pintado. Los paneles con `backdrop-blur`
 *      no recomponen de forma fiable al cambiar una variable CSS por JS, así
 *      que quedaban con los colores viejos hasta que una interacción forzaba
 *      el repintado. De ahí el "se arregla al hacer clic".
 *   3. Solo sobreescribía algunas variables, no las derivadas
 *      (`--primary-strong`, `--sunken`), rompiendo las relaciones de contraste
 *      calculadas en globals.css.
 *
 * La paleta de marca vive ahora en un solo sitio: `src/app/globals.css`.
 * Para cambiarla se editan las variables ahí, no desde la interfaz.
 */

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = "pacio_theme";

/** Clave de la paleta personalizada que ya no se usa (ver nota de arriba). */
const LEGACY_COLORS_KEY = "pacio_custom_colors";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY) as Theme | null;
    const initialTheme = savedTheme === "light" ? "light" : "dark";

    // La clase `dark` ya la puso el script inline de `app/layout.tsx` antes del
    // primer pintado. Aquí solo se sincroniza el estado de React con ella; el
    // `toggle` es idempotente y no provoca un cambio visual.
    setThemeState(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme === "dark");

    // Limpieza para navegadores que ya tenían una paleta guardada: sin esto
    // la clave quedaría huérfana, y los estilos en línea que dejó aplicados
    // sobrevivirían a la sesión actual.
    if (localStorage.getItem(LEGACY_COLORS_KEY)) {
      localStorage.removeItem(LEGACY_COLORS_KEY);
      const root = document.documentElement;
      for (const prop of [
        "--primary",
        "--primary-foreground",
        "--accent",
        "--accent-foreground",
        "--background",
        "--card",
        "--popover",
        "--border",
        "--input",
        "--muted",
        "--ring",
      ]) {
        root.style.removeProperty(prop);
      }
    }
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
